/**
 * Single lead list card used inside LeadsPickerSection.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, RefObject } from 'react';

import { cn } from '@/lib/utils';
import { LeadCountBadge, LeadsListTags } from '@/features/smswiser-campaigns/components/shared';

interface LeadItem {
  id: number;
  name: string;
  count?: number | null;
  performanceTags?: string[];
  usageToday?: number;
  remaining?: number | null;
}

interface LeadCardProps {
  lead: LeadItem;
  isSelected: boolean;
  tagColors?: Record<string, string>;
  usageByListId?: Record<string, number>;
  onSelect: (id: number) => void;
  containerRef: RefObject<HTMLDivElement | null>;
}

export const LeadCard: FC<LeadCardProps> = ({ lead, isSelected, tagColors, usageByListId, onSelect, containerRef }) => {
  const handleClick = () => {
    onSelect(lead.id);
    requestAnimationFrame(() => {
      if (containerRef.current) containerRef.current.scrollTop = 0;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') handleClick();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative flex min-w-0 cursor-pointer flex-col gap-1 rounded-lg border p-3 shadow-sm transition-colors hover:border-primary/50',
        isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border bg-card',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('min-w-0 truncate text-sm font-medium leading-tight', isSelected ? 'text-primary' : 'text-foreground')}>
          {lead.name}
        </span>
        <LeadsListTags performanceTags={lead.performanceTags} tagColors={tagColors} />
      </div>
      <LeadCountBadge
        count={lead.count}
        usage={lead.usageToday ?? usageByListId?.[String(lead.id)]}
        remaining={lead.remaining}
        compact
      />
    </div>
  );
};
